import { Crosshair2Icon, TrashIcon } from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DrunkDrink,
  calculatePermille,
  deleteDrink,
} from "src/lib/slices/alcoholSlice";
import DrinkImage from "../DrinkImage";
import Image from "../Image";
import PhotoPreview from "../PhotoPreview";
import { MapDrawer } from "../RouteMap";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import getLogsFromAlcoholData from "src/lib/getLogsFromAlcoholData";

export default function DrinkLogCard({
  drunkDrink,
  allowDeletion,
  showPhoto = true,
  allowPhotoPreview = true,
  allowMapPreview = true,
  ownData = false,
  alcoholData,
}: {
  drunkDrink: DrunkDrink;
  allowDeletion: boolean;
  showPhoto?: boolean;
  allowPhotoPreview?: boolean;
  allowMapPreview?: boolean;
  ownData?: boolean;
  alcoholData?: any;
}) {
  const dispatch = useDispatch();

  const [clickedDelete, setClickedDelete] = useState(false);

  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);

  const user = useSelector((state: any) => state.user);

  useEffect(() => {
    if (clickedDelete) {
      const timeout = setTimeout(() => {
        setClickedDelete(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [clickedDelete]);

  const deleteDrinkRecord = () => {
    if (clickedDelete) {
      dispatch(deleteDrink(drunkDrink.id));
      dispatch(calculatePermille({ user }));
    }
    setClickedDelete(true);
  };

  return (
    <Card
      style={{
        width: "100%",
        padding: "0.5rem",
        position: "relative",
        maxWidth: "18rem",
        margin: "0 auto",
      }}
      key={drunkDrink.id}
    >
      <CardHeader style={{ padding: 0, paddingBottom: "0.5rem" }}>
        <div
          style={{
            position: "relative",
            width: "100%",
            minWidth: "3rem",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "fit-content",
              minWidth: "3rem",
              margin: "0 auto",
            }}
          >
            <DrinkImage
              drink={drunkDrink.drink}
              alt={drunkDrink.drink.name}
              style={{ height: "4rem", margin: "0 auto" }}
            />
            {drunkDrink.container.icon && (
              <img
                src={`/container-images/${drunkDrink.container.icon}`}
                alt={drunkDrink.container.name}
                style={{
                  position: "absolute",
                  bottom: "-15%",
                  left: "-15%",
                  height: "2rem",
                }}
              />
            )}
          </div>
          {drunkDrink.photoData && showPhoto && (
            <Image
              src={drunkDrink.photoData}
              alt={`Foto uživatele u ${drunkDrink.drink.name}`}
              style={{
                position: "absolute",
                left: "-1.5rem",
                top: "-2rem",
                height: "4rem",
                aspectRatio: "2/3",
                objectFit: "cover",
                rotate: "-15deg",
                cursor: allowPhotoPreview ? "pointer" : undefined,
                borderRadius: "0.5rem",
                zIndex: 0,
              }}
              onClick={() =>
                setPhotoPreviewOpen(allowPhotoPreview ? true : false)
              }
            />
          )}
        </div>
        <div
          style={{
            textAlign: "center",
            color: "hsl(var(--muted-foreground))",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "0.25rem",
            lineHeight: "1.25rem",
          }}
        >
          {new Date(drunkDrink.date).toLocaleDateString("cs") !==
            new Date().toLocaleDateString("cs") && (
            <span>{new Date(drunkDrink.date).toLocaleDateString("cs")}</span>
          )}{" "}
          <span>
            {new Date(drunkDrink.date).toLocaleTimeString("cs", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      </CardHeader>
      <CardContent style={{ padding: 0 }}>
        <h3
          style={{ textAlign: "center", fontWeight: "500", lineHeight: "1rem" }}
        >
          {drunkDrink.drink.name}
        </h3>
        <div
          style={{
            textAlign: "center",
            fontSize: "0.75rem",
            color: "hsl(var(--muted-foreground))",
            lineHeight: "0.75rem",
          }}
        >
          <span style={{ fontSize: "0.5rem" }}>
            {drunkDrink.drink.alcoholVolume} %
          </span>
          <br />
          {drunkDrink.container.name}
          <br />
          <span style={{ fontSize: "0.5rem" }}>
            {drunkDrink.container.volume} {drunkDrink.container.units}
          </span>
        </div>
      </CardContent>
      {allowDeletion && (
        <Button
          style={{
            position: "absolute",
            top: "-0.25rem",
            right: "-0.25rem",
            zIndex: 2,
          }}
          size="icon"
          variant={clickedDelete ? "destructive" : "outline"}
          onClick={deleteDrinkRecord}
        >
          <TrashIcon />
        </Button>
      )}
      {drunkDrink.photoData && allowPhotoPreview && (
        <PhotoPreview
          open={photoPreviewOpen}
          setOpen={setPhotoPreviewOpen}
          imgUrl={drunkDrink.photoData}
        />
      )}
      {drunkDrink.latitude && allowMapPreview && (
        <div
          style={{
            position: "absolute",
            bottom: "0",
            right: "0",
          }}
        >
          <MapDrawer
            logToCenter={{ type: "drink", data: drunkDrink }}
            selectOwnLogs={ownData}
            selectOwnRoute={ownData}
            logs={!ownData ? getLogsFromAlcoholData(alcoholData) : undefined}
            route={!ownData ? alcoholData?.route : undefined}
            asChild
          >
            <Button variant="ghost" size="icon">
              <Crosshair2Icon />
            </Button>
          </MapDrawer>
        </div>
      )}
    </Card>
  );
}
